"use client";

import Link from "next/link";
import {
  ArrowLeft,
  Store,
  TrendingUp,
  Shield,
  Headphones,
  DollarSign,
  Package,
  CheckCircle,
  BarChart3,
  Zap,
  Clock,
  Mail,
  Phone,
  Building2,
  Truck,
  MessageSquare,
  Handshake,
  Globe
} from "lucide-react";
import { COMPANY_DEFAULTS } from "@/lib/constants/companyInfo";
import { useContactInfo } from "@/context/SiteConfigContext";

const benefits = [
  {
    icon: TrendingUp,
    title: "Expand Your Reach",
    description: "Access thousands of customers actively searching for quality window treatments across the country."
  },
  {
    icon: DollarSign,
    title: "Competitive Commission",
    description: "Earn attractive margins on every sale with our transparent and fair pricing structure."
  },
  {
    icon: Package,
    title: "Easy Inventory Management",
    description: "Powerful dashboard to manage products, track orders, and handle shipments efficiently."
  },
  {
    icon: BarChart3,
    title: "Analytics & Insights",
    description: "Real-time sales data and customer insights to help you grow your business."
  },
  {
    icon: Headphones,
    title: "Dedicated Support",
    description: "Get help from our vendor success team whenever you need it. We're invested in your success."
  },
  {
    icon: Shield,
    title: "Secure Payments",
    description: "On-time payments deposited directly to your account with full transaction transparency."
  },
];

const stats = [
  { value: "50K+", label: "Monthly Visitors" },
  { value: "$5M+", label: "Annual GMV" },
  { value: "98%", label: "Vendor Satisfaction" },
];

const requirements = [
  "Established window treatment manufacturing or distribution business",
  "Quality products that meet our standards and specifications",
  "Ability to fulfill custom orders within 5-10 business days",
  "Commitment to excellent customer service",
  "Valid business license and liability insurance",
  "Competitive pricing aligned with market standards",
];

const vendorTypes = [
  {
    title: "Manufacturers",
    description: "Produce and ship directly to customers",
    features: ["Full product control", "Higher margins", "Brand visibility"],
    icon: Building2
  },
  {
    title: "Blinds & Shades Installers",
    description: "Professional installation services",
    features: ["Local service coverage", "Installation expertise", "Customer referrals"],
    icon: Truck
  },
];


export default function VendorsPage() {
  const { email } = useContactInfo();
  const salesEmail = email.sales;

  const faqs = [
    {
      question: "How do I become a vendor?",
      answer: `Simply email us at ${salesEmail} with your business details. Our team will review your application and get back to you within 2-3 business days.`
    },
    {
      question: "What are the fees and commissions?",
      answer: "We operate on a competitive commission structure that varies based on product category and volume. Contact our sales team for detailed pricing information."
    },
    {
      question: "How do payments work?",
      answer: "Vendors receive payments bi-weekly via direct deposit. All transactions are tracked in your vendor dashboard with complete transparency."
    },
    {
      question: "Can I set my own prices?",
      answer: "Yes, vendors have full control over their pricing. We provide market insights and recommendations, but final pricing decisions are yours."
    },
    {
      question: "What support do you provide?",
      answer: "Every vendor gets a dedicated account manager, access to our vendor success team, marketing support, and comprehensive training resources."
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[var(--primary-red)] text-white py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <Link href="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full mb-4">
                <Store className="w-4 h-4" />
                Vendor Partner Program
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
                Grow Your Business With {COMPANY_DEFAULTS.name}
              </h1>
              <p className="text-lg sm:text-xl text-white/90 mb-6 sm:mb-8 leading-relaxed">
                Join our marketplace and reach thousands of customers looking for quality window treatments.
                We provide the platform, marketing, and support — you provide great products.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`mailto:${salesEmail}?subject=Vendor Partnership Inquiry`}
                  className="inline-flex items-center justify-center gap-2 bg-white text-[var(--primary-red)] font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl text-base"
                >
                  <Mail className="w-5 h-5" />
                  Contact Sales Team
                </a>
                <a
                  href={COMPANY_DEFAULTS.phoneTel}
                  className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-lg border border-white/30 hover:bg-white/20 transition-all text-base"
                >
                  <Phone className="w-5 h-5" />
                  {COMPANY_DEFAULTS.phone}
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center gap-3 mb-6">
                  <Handshake className="w-8 h-8" />
                  <h3 className="text-xl font-bold">Partner With Us</h3>
                </div>
                <div className="space-y-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Email our sales team</p>
                      <a href={`mailto:${salesEmail}`} className="font-semibold hover:underline text-lg">
                        {salesEmail}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Call us directly</p>
                      <a href={COMPANY_DEFAULTS.phoneTel} className="font-semibold hover:underline text-lg">
                        {COMPANY_DEFAULTS.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Response time</p>
                      <p className="font-semibold text-lg">Within 24 hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-gray-900 py-6 sm:py-10">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm sm:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <section className="mb-12 sm:mb-20">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Why Partner With Us?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
              We've built a platform designed to help window treatment vendors succeed and grow their business.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-red-100 transition-all group">
                <div className="w-14 h-14 bg-[var(--primary-red)]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[var(--primary-dark)] transition-colors">
                  <benefit.icon className="w-7 h-7 text-[var(--primary-red)] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Vendor Types */}
        <section className="mb-12 sm:mb-20">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Who Can Join?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
              We partner with various types of window treatment businesses.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {vendorTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-5">
                  <type.icon className="w-7 h-7 text-gray-700" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{type.title}</h3>
                <p className="text-gray-600 mb-5">{type.description}</p>
                <ul className="space-y-2">
                  {type.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3 text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-12 sm:mb-20 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 md:p-14">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">How It Works</h2>
            <p className="text-gray-600 text-base sm:text-lg">Getting started is simple. Here's what to expect.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10">
            {[
              { step: "1", icon: Mail, title: "Apply", desc: `Email ${salesEmail} with your business details` },
              { step: "2", icon: Clock, title: "Review", desc: "Our team reviews your application within 2-3 business days" },
              { step: "3", icon: Zap, title: "Onboard", desc: "Set up your vendor dashboard and upload your products" },
              { step: "4", icon: TrendingUp, title: "Sell & Grow", desc: "Start receiving orders and watch your business grow" },
            ].map((item, index) => (
              <div key={index} className="text-center relative">
                {index < 3 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-gray-200" />
                )}
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[var(--primary-red)] text-white rounded-full flex items-center justify-center mx-auto mb-4 relative z-10 shadow-lg">
                  <item.icon className="w-7 h-7 sm:w-9 sm:h-9" />
                </div>
                <div className="text-sm text-[var(--primary-red)] font-bold mb-1">Step {item.step}</div>
                <h3 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-12 sm:mb-20">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
              Everything you need to know about becoming a vendor.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-[var(--primary-red)] flex-shrink-0 mt-0.5" />
                  {faq.question}
                </h3>
                <p className="text-gray-600 ml-8">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Requirements */}
        <section className="mb-12 sm:mb-20">
          <div className="bg-red-50 rounded-2xl border border-red-100 p-6 sm:p-10 max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center">Vendor Requirements</h2>
            <ul className="space-y-4">
              {requirements.map((req, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{req}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gray-800 rounded-2xl p-8 sm:p-14 text-white">
          <div className="text-center max-w-3xl mx-auto">
            <Globe className="w-16 h-16 mx-auto mb-6 text-[var(--primary-red)]" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Ready to Grow Your Business?</h2>
            <p className="text-white/80 mb-8 text-base sm:text-lg">
              Join hundreds of successful vendors already selling on {COMPANY_DEFAULTS.name}.
              Our team will guide you through every step of the onboarding process.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href={`mailto:${salesEmail}?subject=Vendor Partnership Inquiry`}
                className="inline-flex items-center gap-2 bg-[var(--primary-red)] text-white px-8 py-4 rounded-lg font-bold hover:bg-[var(--primary-dark)] transition-all shadow-lg hover:shadow-xl text-base w-full sm:w-auto justify-center"
              >
                <Mail className="w-5 h-5" />
                Email Sales Team
              </a>
              <a
                href={COMPANY_DEFAULTS.phoneTel}
                className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all border border-white/30 w-full sm:w-auto justify-center"
              >
                <Phone className="w-5 h-5" />
                Call {COMPANY_DEFAULTS.phone}
              </a>
            </div>
            <div className="pt-8 border-t border-white/20">
              <p className="text-white/60 mb-3">Contact our sales team directly:</p>
              <a href={`mailto:${salesEmail}`} className="text-xl sm:text-2xl font-bold text-white hover:text-[var(--primary-red)] transition-colors">
                {salesEmail}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
